import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Wallet, Download, FileText, Plus, Loader2, AlertTriangle, Users, Calculator, Settings, History, ArrowUp, ArrowDown, Minus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayrollPage = () => {
  const { isHR, user } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  const [isForm16DialogOpen, setIsForm16DialogOpen] = useState(false);
  const [isGenerateOpen, setIsGenerateOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateEmpId, setGenerateEmpId] = useState('');
  const [generateMonth, setGenerateMonth] = useState(new Date().toISOString().slice(0, 7));
  const [form16Year, setForm16Year] = useState('2025-26');
  const [form16EmployeeId, setForm16EmployeeId] = useState('');

  // CTC-based salary creation
  const [isCTCOpen, setIsCTCOpen] = useState(false);
  const [ctcSubmitting, setCtcSubmitting] = useState(false);
  const [ctcForm, setCtcForm] = useState({
    employee_id: '', ctc: '', medical_insurance: '0',
  });
  const [ctcPreview, setCtcPreview] = useState(null);

  // Bulk generate
  const [isBulkGenOpen, setIsBulkGenOpen] = useState(false);
  const [bulkMonth, setBulkMonth] = useState(new Date().toISOString().slice(0, 7));
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [activeTab, setActiveTab] = useState('payslips');

  // Payslip Adjustments (one-off credits/debits like TDS refunds)
  const [adjRows, setAdjRows] = useState([]); // [{amount, description, type}]
  const [existingAdjustments, setExistingAdjustments] = useState([]);
  const [savingAdj, setSavingAdj] = useState(false);

  // Salary Revision History
  const [revisionEmpId, setRevisionEmpId] = useState('');
  const [revisionData, setRevisionData] = useState(null);
  const [isRevisionOpen, setIsRevisionOpen] = useState(false);

  const fetchPayslips = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payslips?month=${selectedMonth}`, { withCredentials: true });
      setPayslips(response.data);
    } catch (error) {
      console.error('Fetch payslips error:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedMonth]);

  useEffect(() => {
    fetchPayslips();
    if (isHR) {
      Promise.all([
        axios.get(`${API_URL}/api/employees`, { withCredentials: true }),
        axios.get(`${API_URL}/api/salary-structures`, { withCredentials: true }),
      ]).then(([empRes, salRes]) => {
        setEmployees(empRes.data);
        setSalaryStructures(salRes.data);
      }).catch(() => {});
    }
  }, [fetchPayslips, isHR]);

  const generatePayslip = async () => {
    if (!generateEmpId) { toast.error('Select an employee'); return; }
    setGenerating(true);
    try {
      // 1. Persist any newly-entered adjustment rows first
      const rowsToSave = adjRows.filter(r => r.amount && parseFloat(r.amount) > 0 && (r.description || '').trim().length >= 3);
      for (const r of rowsToSave) {
        await axios.post(`${API_URL}/api/payslip-adjustments`, {
          employee_id: generateEmpId,
          month: generateMonth,
          amount: parseFloat(r.amount),
          description: r.description.trim(),
          type: r.type || 'credit',
        }, { withCredentials: true });
      }
      // 2. Generate — backend auto-picks up pending adjustments for this employee+month
      const res = await axios.post(`${API_URL}/api/payslips/generate`, {
        employee_id: generateEmpId, month: generateMonth
      }, { withCredentials: true });
      const data = res.data;
      const adjSuffix = data.adjustments_total ? ` · adjustments ${data.adjustments_total > 0 ? '+' : ''}₹${data.adjustments_total.toLocaleString()}` : '';
      toast.success(`Payslip generated! Net: INR ${data.net_salary?.toLocaleString()}${data.lop_days > 0 ? ` (${data.lop_days} LOP days)` : ''}${adjSuffix}`);
      setIsGenerateOpen(false);
      setAdjRows([]);
      setExistingAdjustments([]);
      setSelectedMonth(generateMonth);
      fetchPayslips();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate payslip');
    } finally {
      setGenerating(false);
    }
  };

  const fetchExistingAdjustments = useCallback(async (empId, month) => {
    if (!empId || !month) { setExistingAdjustments([]); return; }
    try {
      const res = await axios.get(`${API_URL}/api/payslip-adjustments`, {
        params: { employee_id: empId, month },
        withCredentials: true,
      });
      setExistingAdjustments(res.data?.adjustments || []);
    } catch { setExistingAdjustments([]); }
  }, []);

  const deleteExistingAdjustment = async (adjId) => {
    try {
      setSavingAdj(true);
      await axios.delete(`${API_URL}/api/payslip-adjustments/${adjId}`, { withCredentials: true });
      toast.success('Adjustment removed');
      fetchExistingAdjustments(generateEmpId, generateMonth);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally { setSavingAdj(false); }
  };

  useEffect(() => {
    if (isGenerateOpen && generateEmpId && generateMonth) {
      fetchExistingAdjustments(generateEmpId, generateMonth);
    }
  }, [isGenerateOpen, generateEmpId, generateMonth, fetchExistingAdjustments]);

  const downloadPayslipPdf = async (payslipId, employeeId) => {
    setDownloadingPdf(payslipId);
    try {
      const response = await axios.get(`${API_URL}/api/payslips/${payslipId}/pdf`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${employeeId}_${selectedMonth}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch (error) {
      toast.error('Failed to download payslip');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const downloadForm16 = async () => {
    if (!form16EmployeeId) { toast.error('Select an employee'); return; }
    try {
      const response = await axios.get(`${API_URL}/api/compliance/form16/${form16EmployeeId}/pdf?financial_year=${form16Year}`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `form16_${form16Year}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Form 16 downloaded');
      setIsForm16DialogOpen(false);
    } catch (error) {
      toast.error('Failed to download Form 16');
    }
  };

  const calculateCTCPreview = (ctc, medicalMonthly) => {
    if (!ctc || ctc <= 0) return null;
    const monthly = ctc / 12;
    const basic = Math.round(monthly * 0.40);
    const hra = Math.round(basic * 0.50);
    const employerPf = Math.round(basic * 0.12);
    const gratuityMonthly = Math.round(basic * 0.0481);
    const gratuityAnnual = gratuityMonthly * 12;
    const med = Math.round(parseFloat(medicalMonthly) || 0);
    const gross = Math.round(monthly - employerPf - med - gratuityMonthly);
    const special = Math.max(0, Math.round(gross - basic - hra));
    const employeePf = Math.round(basic * 0.12);
    const pt = 200;
    const net = Math.round(gross - employeePf - pt);
    return { basic, hra, special, employerPf, employeePf, pt, med, gratuityMonthly, gratuityAnnual, gross, net };
  };

  const handleCTCChange = (field, value) => {
    const newForm = { ...ctcForm, [field]: value };
    setCtcForm(newForm);
    const preview = calculateCTCPreview(
      parseFloat(newForm.ctc) || 0, newForm.medical_insurance
    );
    setCtcPreview(preview);
  };

  const handleSubmitCTC = async () => {
    if (!ctcForm.employee_id || !ctcForm.ctc) { toast.error('Select employee and enter CTC'); return; }
    setCtcSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/salary-structures/from-ctc`, {
        employee_id: ctcForm.employee_id,
        ctc: parseFloat(ctcForm.ctc),
        medical_insurance: parseFloat(ctcForm.medical_insurance) || 0,
      }, { withCredentials: true });
      toast.success('Salary structure created from CTC');
      setIsCTCOpen(false);
      setCtcForm({ employee_id: '', ctc: '', medical_insurance: '0' });
      setCtcPreview(null);
      // Refresh
      const salRes = await axios.get(`${API_URL}/api/salary-structures`, { withCredentials: true });
      setSalaryStructures(salRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create salary structure');
    } finally {
      setCtcSubmitting(false);
    }
  };

  const handleBulkGenerate = async () => {
    setBulkGenerating(true);
    setBulkResult(null);
    try {
      const res = await axios.post(`${API_URL}/api/payslips/generate-month`, { month: bulkMonth }, { withCredentials: true });
      setBulkResult(res.data);
      toast.success(`Generated ${res.data.generated} payslips, skipped ${res.data.skipped}`);
      setSelectedMonth(bulkMonth);
      fetchPayslips();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate payslips');
    } finally {
      setBulkGenerating(false);
    }
  };

  const formatCurrency = (amount) => `INR ${(amount || 0).toLocaleString('en-IN')}`;
  // Return the LATEST salary structure for an employee (by effective_from desc).
  // Necessary because migrations create additional structures per employee; we must show the current one.
  const getEmpSalary = (empId) => {
    const rows = salaryStructures.filter(s => s.employee_id === empId);
    if (rows.length === 0) return undefined;
    return rows.reduce((latest, s) => {
      if (!latest) return s;
      return (s.effective_from || '') > (latest.effective_from || '') ? s : latest;
    }, null);
  };

  const fetchRevisions = async (empId) => {
    if (!empId) return;
    try {
      const res = await axios.get(`${API_URL}/api/salary-revisions/${empId}`, { withCredentials: true });
      setRevisionData(res.data);
      setIsRevisionOpen(true);
    } catch (err) {
      toast.error('Failed to fetch salary revisions');
    }
  };

  const deletePayslip = async (payslipId) => {
    if (!window.confirm('Delete this payslip? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/payslips/${payslipId}`, { withCredentials: true });
      toast.success('Payslip deleted');
      fetchPayslips();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete payslip');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Wallet className="w-7 h-7 text-primary" />Payroll Management</h1>
          <p className="text-muted-foreground">Manage salaries, generate payslips, and download Form 16</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="w-[180px]" data-testid="month-selector" />
          {isHR && (
            <>
              <Button size="sm" variant="outline" onClick={() => { setCtcForm({ employee_id: '', ctc: '', medical_insurance: '0' }); setCtcPreview(null); setIsCTCOpen(true); }} className="gap-1.5" data-testid="set-ctc-btn">
                <Calculator className="w-4 h-4" /> Set CTC
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setBulkMonth(selectedMonth); setBulkResult(null); setIsBulkGenOpen(true); }} className="gap-1.5" data-testid="bulk-generate-btn">
                <Users className="w-4 h-4" /> Generate All
              </Button>
              <Button size="sm" onClick={() => { setGenerateEmpId(''); setGenerateMonth(selectedMonth); setIsGenerateOpen(true); }} className="gap-1.5" data-testid="generate-payslip-btn">
                <Plus className="w-4 h-4" /> Generate Single
              </Button>
            </>
          )}
          <Dialog open={isForm16DialogOpen} onOpenChange={setIsForm16DialogOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" className="gap-1.5" data-testid="form16-btn"><FileText className="w-4 h-4" />Form 16</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Download Form 16</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2"><Label>Financial Year</Label>
                  <Select value={form16Year} onValueChange={setForm16Year}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="2025-26">FY 2025-26</SelectItem><SelectItem value="2024-25">FY 2024-25</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Employee</Label>
                  {isHR ? (
                    <Select value={form16EmployeeId} onValueChange={setForm16EmployeeId}><SelectTrigger data-testid="form16-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                      <SelectContent>{employees.map(emp => (<SelectItem key={emp.id} value={emp.id}>{emp.name} — {emp.employee_id}</SelectItem>))}</SelectContent>
                    </Select>
                  ) : (<Input value={form16EmployeeId} onChange={(e) => setForm16EmployeeId(e.target.value)} placeholder="Enter employee UUID" />)}
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setIsForm16DialogOpen(false)}>Cancel</Button>
                  <Button onClick={downloadForm16} className="gap-2" data-testid="download-form16"><Download className="w-4 h-4" />Download</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {isHR ? (
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="payslips" data-testid="tab-payslips"><FileText className="w-4 h-4 mr-1.5" />Payslips</TabsTrigger>
            <TabsTrigger value="structures" data-testid="tab-structures"><Settings className="w-4 h-4 mr-1.5" />Salary Structures</TabsTrigger>
            <TabsTrigger value="revisions" data-testid="tab-revisions"><History className="w-4 h-4 mr-1.5" />Revision History</TabsTrigger>
          </TabsList>
          <TabsContent value="payslips" className="mt-4">{renderPayslips()}</TabsContent>
          <TabsContent value="structures" className="mt-4">{renderSalaryStructures()}</TabsContent>
          <TabsContent value="revisions" className="mt-4">{renderRevisionHistory()}</TabsContent>
        </Tabs>
      ) : renderPayslips()}

      {/* Generate Single Payslip Dialog */}
      <Dialog open={isGenerateOpen} onOpenChange={(open) => { setIsGenerateOpen(open); if (!open) { setAdjRows([]); setExistingAdjustments([]); } }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Plus className="w-5 h-5" /> Generate Payslip</DialogTitle><DialogDescription>Auto-calculates LOP deductions based on leave data. Add optional one-off adjustments below.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Employee</Label><Select value={generateEmpId} onValueChange={setGenerateEmpId}><SelectTrigger data-testid="gen-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger><SelectContent>{employees.map(emp => (<SelectItem key={emp.id} value={emp.id}>{emp.name} — {emp.employee_id}</SelectItem>))}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Month</Label><Input type="month" value={generateMonth} onChange={(e) => setGenerateMonth(e.target.value)} data-testid="gen-month" /></div>
            <div className="bg-muted/50 p-3 rounded-lg text-xs text-muted-foreground"><div className="flex items-center gap-1.5 mb-1"><AlertTriangle className="w-3.5 h-3.5" /><span className="font-medium">LOP + Per-diem Auto-Calculation</span></div><p>LOP days are deducted from gross. Per-diem allowance from approved travel is added.</p></div>

            {/* Adjustments panel */}
            {generateEmpId && (
              <div className="border rounded-lg p-3 space-y-2" data-testid="gen-adjustments-panel">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-semibold">Adjustments (credits / debits)</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setAdjRows(prev => [...prev, { amount: '', description: '', type: 'credit' }])}
                    className="h-7 gap-1 text-xs"
                    data-testid="add-adjustment-row-btn"
                  >
                    <Plus className="w-3 h-3" /> Add row
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Any pending adjustments for this employee &amp; month will be applied on generate. Description appears on the payslip PDF as legal proof.
                </p>

                {existingAdjustments.length > 0 && (
                  <div className="space-y-1 pb-2 border-b">
                    <p className="text-[11px] font-medium text-muted-foreground">Already saved (will apply on generate):</p>
                    {existingAdjustments.map(a => (
                      <div key={a.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`existing-adj-${a.id}`}>
                        <div className="flex-1 min-w-0">
                          <span className={a.type === 'credit' ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-700 dark:text-rose-400'}>
                            {a.type === 'credit' ? '+' : '−'} ₹{Number(a.amount).toLocaleString('en-IN')}
                          </span>
                          <span className="text-muted-foreground ml-2 truncate">{a.description}</span>
                        </div>
                        <Button type="button" size="sm" variant="ghost" onClick={() => deleteExistingAdjustment(a.id)} disabled={savingAdj} className="h-6 px-1.5 text-xs text-rose-600" data-testid={`delete-adj-${a.id}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {adjRows.length === 0 && existingAdjustments.length === 0 && (
                  <p className="text-[11px] text-muted-foreground italic">No adjustments — click &quot;Add row&quot; if you need to credit or debit an amount (e.g. TDS refund).</p>
                )}

                {adjRows.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-[110px_1fr_100px_32px] gap-2 items-center" data-testid={`adj-row-${idx}`}>
                    <Input
                      type="number"
                      placeholder="Amount"
                      value={row.amount}
                      onChange={e => setAdjRows(prev => prev.map((r, i) => i === idx ? { ...r, amount: e.target.value } : r))}
                      className="h-8 text-xs"
                      min="0" step="0.01"
                      data-testid={`adj-amount-${idx}`}
                    />
                    <Input
                      type="text"
                      placeholder="Description (e.g. TDS refund for July 2025 - wrongly deducted)"
                      value={row.description}
                      onChange={e => setAdjRows(prev => prev.map((r, i) => i === idx ? { ...r, description: e.target.value } : r))}
                      className="h-8 text-xs"
                      data-testid={`adj-desc-${idx}`}
                    />
                    <Select
                      value={row.type}
                      onValueChange={val => setAdjRows(prev => prev.map((r, i) => i === idx ? { ...r, type: val } : r))}
                    >
                      <SelectTrigger className="h-8 text-xs" data-testid={`adj-type-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="credit">Credit (+)</SelectItem>
                        <SelectItem value="debit">Debit (−)</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setAdjRows(prev => prev.filter((_, i) => i !== idx))} className="h-8 w-8 p-0 text-rose-600" data-testid={`adj-remove-${idx}`}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setIsGenerateOpen(false)}>Cancel</Button><Button onClick={generatePayslip} disabled={generating || !generateEmpId} className="gap-2" data-testid="confirm-generate-btn">{generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}Generate</Button></div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Set CTC Dialog */}
      <Dialog open={isCTCOpen} onOpenChange={setIsCTCOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator className="w-5 h-5" /> Set Employee CTC</DialogTitle><DialogDescription>Enter annual CTC and the system will auto-calculate salary components</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Employee</Label><Select value={ctcForm.employee_id} onValueChange={(v) => handleCTCChange('employee_id', v)}><SelectTrigger data-testid="ctc-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger><SelectContent>{employees.map(emp => (<SelectItem key={emp.id} value={emp.id}>{emp.name} — {emp.employee_id}</SelectItem>))}</SelectContent></Select></div>
            <div className="space-y-2"><Label>Annual CTC (INR)</Label><Input type="number" value={ctcForm.ctc} onChange={(e) => handleCTCChange('ctc', e.target.value)} placeholder="e.g., 1200000" data-testid="ctc-amount" /></div>
            <div className="space-y-2"><Label>Monthly Medical Insurance (INR)</Label><Input type="number" value={ctcForm.medical_insurance} onChange={(e) => handleCTCChange('medical_insurance', e.target.value)} placeholder="e.g., 4000" data-testid="ctc-medical-insurance" /></div>
            {ctcPreview && (
              <div className="bg-muted/50 p-4 rounded-lg space-y-2 text-sm">
                <p className="font-semibold text-xs uppercase text-muted-foreground mb-2">Monthly Breakdown (Auto-calculated)</p>
                <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                  <span className="text-muted-foreground">Basic (40% CTC):</span><span className="font-mono">{formatCurrency(ctcPreview.basic)}</span>
                  <span className="text-muted-foreground">HRA (50% Basic):</span><span className="font-mono">{formatCurrency(ctcPreview.hra)}</span>
                  <span className="text-muted-foreground">Special Allowance:</span><span className="font-mono">{formatCurrency(ctcPreview.special)}</span>
                  <span className="text-muted-foreground font-semibold">Gross Salary:</span><span className="font-mono font-semibold">{formatCurrency(ctcPreview.gross)}</span>
                  <div className="col-span-2 border-t my-1" />
                  <span className="text-violet-600 dark:text-violet-400">Employer PF (12% Basic):</span><span className="font-mono text-violet-600 dark:text-violet-400">{formatCurrency(ctcPreview.employerPf)}</span>
                  {ctcPreview.med > 0 && <><span className="text-violet-600 dark:text-violet-400">Medical Insurance:</span><span className="font-mono text-violet-600 dark:text-violet-400">{formatCurrency(ctcPreview.med)}</span></>}
                  <span className="text-violet-600 dark:text-violet-400">Gratuity (4.81% Basic):</span><span className="font-mono text-violet-600 dark:text-violet-400" data-testid="ctc-gratuity-monthly">{formatCurrency(ctcPreview.gratuityMonthly)} <span className="text-[10px] text-muted-foreground">/mo (₹{ctcPreview.gratuityAnnual.toLocaleString('en-IN')} p.a.)</span></span>
                  <div className="col-span-2 border-t my-1" />
                  <span className="text-destructive/70">Employee PF (12% Basic):</span><span className="font-mono text-destructive">-{formatCurrency(ctcPreview.employeePf)}</span>
                  <span className="text-destructive/70">Prof. Tax:</span><span className="font-mono text-destructive">-{formatCurrency(ctcPreview.pt)}</span>
                  <div className="col-span-2 border-t my-1" />
                  <span className="font-semibold">Net Take Home:</span><span className="font-mono font-bold text-[hsl(var(--success))]">{formatCurrency(ctcPreview.net)}</span>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsCTCOpen(false)}>Cancel</Button>
              <Button onClick={handleSubmitCTC} disabled={ctcSubmitting || !ctcForm.employee_id || !ctcForm.ctc} data-testid="submit-ctc-btn">
                {ctcSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Create Salary Structure
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Generate Dialog */}
      <Dialog open={isBulkGenOpen} onOpenChange={setIsBulkGenOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Generate Payslips for All Employees</DialogTitle><DialogDescription>Creates payslips for all active employees with salary structures</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Month</Label><Input type="month" value={bulkMonth} onChange={(e) => setBulkMonth(e.target.value)} data-testid="bulk-gen-month" /></div>
            {bulkResult && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Generated</p><p className="text-xl font-bold text-[hsl(var(--success))]">{bulkResult.generated}</p></CardContent></Card>
                  <Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Skipped</p><p className="text-xl font-bold text-amber-500">{bulkResult.skipped}</p></CardContent></Card>
                </div>
                {bulkResult.skipped_details?.length > 0 && (
                  <div className="bg-muted/50 p-3 rounded-lg text-xs max-h-40 overflow-y-auto">
                    <p className="font-medium mb-1">Skipped employees:</p>
                    {bulkResult.skipped_details.map((s, i) => (
                      <p key={i} className="text-muted-foreground">{s.name || s.employee_id} — {s.reason}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsBulkGenOpen(false)}>Close</Button>
              <Button onClick={handleBulkGenerate} disabled={bulkGenerating} className="gap-2" data-testid="confirm-bulk-generate-btn">
                {bulkGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
                Generate All
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );

  function renderPayslips() {
    return (
      <>
        {payslips.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
            <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total Gross</p><p className="text-2xl font-bold">{formatCurrency(payslips.reduce((s, p) => s + p.gross_salary, 0))}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total Net</p><p className="text-2xl font-bold text-[hsl(var(--success))]">{formatCurrency(payslips.reduce((s, p) => s + p.net_salary, 0))}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Total TDS</p><p className="text-2xl font-bold">{formatCurrency(payslips.reduce((s, p) => s + p.tds, 0))}</p></CardContent></Card>
            <Card><CardContent className="p-4"><p className="text-sm text-muted-foreground">Payslips</p><p className="text-2xl font-bold">{payslips.length}</p></CardContent></Card>
          </div>
        )}
        <Card>
          <CardHeader><CardTitle>Payslips - {new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</CardTitle></CardHeader>
          <CardContent>
            {payslips.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead><tr>
                    <th>Employee</th><th>Basic</th><th>HRA</th><th>Gross</th>
                    <th>LOP</th><th>Reimb.</th><th>Emp. PF</th><th>TDS</th><th>Net</th><th>PDF</th>{isHR && <th></th>}
                  </tr></thead>
                  <tbody>
                    {payslips.map((p) => (
                      <tr key={p.id}>
                        <td className="font-medium text-sm">
                          {p.employee_name || p.employee_id.slice(0, 8)}
                          {p.joining_prorate && <Badge variant="outline" className="ml-1 text-[10px]">Pro-rata</Badge>}
                        </td>
                        <td className="font-mono text-xs">{formatCurrency(p.basic_salary)}</td>
                        <td className="font-mono text-xs">{formatCurrency(p.hra)}</td>
                        <td className="font-mono text-xs font-medium">{formatCurrency(p.gross_salary)}</td>
                        <td>{p.lop_days > 0 ? <Badge variant="destructive" className="text-xs">{p.lop_days}d</Badge> : <span className="text-xs text-muted-foreground">0</span>}</td>
                        <td className="font-mono text-xs">{(p.reimbursement_total || p.per_diem_total || 0) > 0 ? <span className="text-[hsl(var(--success))]">+{formatCurrency(p.reimbursement_total || p.per_diem_total)}</span> : '—'}</td>
                        <td className="font-mono text-xs text-destructive">{formatCurrency(p.employee_pf || p.pf_deduction)}</td>
                        <td className="font-mono text-xs text-destructive">{formatCurrency(p.tds)}</td>
                        <td className="font-mono font-bold text-[hsl(var(--success))]">{formatCurrency(p.net_salary)}</td>
                        <td><Button variant="ghost" size="sm" onClick={() => downloadPayslipPdf(p.id, p.employee_id.slice(0, 8))} disabled={downloadingPdf === p.id} className="gap-1">{downloadingPdf === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}</Button></td>
                        {isHR && <td><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => deletePayslip(p.id)} data-testid={`delete-payslip-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button></td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">No payslips for this month. {isHR && 'Use "Generate All" to create payslips.'}</p>
            )}
          </CardContent>
        </Card>
      </>
    );
  }

  function renderSalaryStructures() {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Employee Salary Structures</CardTitle>
          <CardDescription>CTC and monthly component breakdown for each employee</CardDescription>
        </CardHeader>
        <CardContent>
          {employees.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead><tr>
                  <th>Employee</th><th>CTC (Annual)</th><th>Basic/mo</th><th>HRA/mo</th><th>Special/mo</th><th>Employer PF</th><th>Gratuity/mo</th><th>Emp. PF</th><th>Status</th>
                </tr></thead>
                <tbody>
                  {employees.map(emp => {
                    const sal = getEmpSalary(emp.id);
                    return (
                      <tr key={emp.id} data-testid={`salary-row-${emp.id}`}>
                        <td className="font-medium text-sm">{emp.name}</td>
                        <td className="font-mono">{sal?.ctc ? formatCurrency(sal.ctc) : <span className="text-muted-foreground text-xs">Not set</span>}</td>
                        <td className="font-mono text-xs">{sal ? formatCurrency(sal.basic_salary) : '—'}</td>
                        <td className="font-mono text-xs">{sal ? formatCurrency(sal.hra) : '—'}</td>
                        <td className="font-mono text-xs">{sal ? formatCurrency(sal.special_allowance) : '—'}</td>
                        <td className="font-mono text-xs text-violet-600 dark:text-violet-400">{sal ? formatCurrency(sal.employer_pf) : '—'}</td>
                        <td className="font-mono text-xs text-violet-600 dark:text-violet-400" data-testid={`gratuity-cell-${emp.id}`}>{sal?.gratuity_monthly ? formatCurrency(sal.gratuity_monthly) : '—'}</td>
                        <td className="font-mono text-xs text-destructive">{sal ? formatCurrency(sal.employee_pf || sal.pf_contribution) : '—'}</td>
                        <td>{sal ? <Badge className="badge-success text-xs">Active</Badge> : <Badge variant="outline" className="text-xs">Pending</Badge>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p className="text-center text-muted-foreground py-8">No employees found</p>}
        </CardContent>
      </Card>
    );
  }

  function renderRevisionHistory() {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Salary Revision History</CardTitle>
          <CardDescription>Track CTC changes per employee with effective dates — useful for appraisals and audits</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Select Employee</Label>
                <Select value={revisionEmpId} onValueChange={(v) => { setRevisionEmpId(v); fetchRevisions(v); }}>
                  <SelectTrigger data-testid="revision-employee-select"><SelectValue placeholder="Select employee to view revisions" /></SelectTrigger>
                  <SelectContent>{employees.map(emp => (<SelectItem key={emp.id} value={emp.id}>{emp.name} — {emp.employee_id}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            {revisionData && revisionData.revisions?.length > 0 ? (
              <div className="space-y-0">
                <p className="text-sm font-medium mb-3">{revisionData.employee_name} ({revisionData.emp_code}) — {revisionData.revisions.length} revision(s)</p>
                <div className="relative pl-6 border-l-2 border-primary/20 space-y-4">
                  {revisionData.revisions.map((rev, idx) => {
                    const prevRev = idx < revisionData.revisions.length - 1 ? revisionData.revisions[idx + 1] : null;
                    const revCtc = rev.ctc || ((rev.basic_salary + rev.hra + (rev.special_allowance || 0) + (rev.da || 0)) * 12);
                    const prevCtc = prevRev ? (prevRev.ctc || ((prevRev.basic_salary + prevRev.hra + (prevRev.special_allowance || 0) + (prevRev.da || 0)) * 12)) : 0;
                    const ctcChange = prevRev ? revCtc - prevCtc : 0;
                    const pctChange = prevRev && prevCtc > 0 ? ((ctcChange / prevCtc) * 100).toFixed(1) : null;
                    return (
                      <div key={rev.id || idx} className="relative" data-testid={`revision-${idx}`}>
                        <div className={`absolute -left-[25px] w-4 h-4 rounded-full border-2 ${idx === 0 ? 'bg-primary border-primary' : 'bg-background border-primary/40'}`} />
                        <div className={`p-3 rounded-lg ${idx === 0 ? 'bg-primary/5 border border-primary/20' : 'bg-muted/30'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold">{formatCurrency(revCtc)} /yr</span>
                              {idx === 0 && <Badge className="text-[10px]">Current</Badge>}
                              {ctcChange !== 0 && (
                                <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${ctcChange > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                  {ctcChange > 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                                  {pctChange}%
                                </span>
                              )}
                              {ctcChange === 0 && prevRev && <span className="inline-flex items-center gap-0.5 text-xs text-muted-foreground"><Minus className="w-3 h-3" />No change</span>}
                            </div>
                            <span className="text-xs text-muted-foreground">Effective: {rev.effective_from}</span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
                            <span className="text-muted-foreground">Basic: <span className="font-mono text-foreground">{formatCurrency(rev.basic_salary)}</span></span>
                            <span className="text-muted-foreground">HRA: <span className="font-mono text-foreground">{formatCurrency(rev.hra)}</span></span>
                            <span className="text-muted-foreground">Special: <span className="font-mono text-foreground">{formatCurrency(rev.special_allowance)}</span></span>
                            <span className="text-muted-foreground">Emp PF: <span className="font-mono text-foreground">{formatCurrency(rev.employee_pf || rev.pf_contribution)}</span></span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : revisionEmpId ? (
              <p className="text-center text-muted-foreground py-6">No salary revisions found for this employee. Set CTC first.</p>
            ) : (
              <p className="text-center text-muted-foreground py-6">Select an employee to view their salary revision timeline.</p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
};

export default PayrollPage;
